import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
// components
import SubmitButton from '../submit-button';
// services
import { formatPrice } from '@services/format';
import { getCookie } from '@services/cookies';
import couponCodeService from '@src/utils/couponCodeService';
// hooks
import { useQueryParamBoolean } from '@hooks/useQueryParams';
import { useShopifyCheckoutAttributes } from '@hooks/useShopifyCheckoutAttributes';
// styles
import styles from './add-to-basket.module.scss';
// constants
import { OFFER_CODES } from '@src/constants';
import { OUT_OF_STOCK } from '@src/constants/messages';
import { COOKIES } from '@src/constants/cookies';
import { getShopifyHost } from '@src/utils/shopify-host-redirect';
import {
  appendShopifyAttributesToUrl,
  appendStoredTrackingParamsToUrl,
} from '@src/utils/shopify-tracking-params';
import { IProductPlan } from '@src/types/product';

interface IProps {
  label?: string;
  isSmallAddToBasketButton?: boolean;
  isReorderButton?: boolean;
  sku?: string;
  plans?: any;
  isFreeTrial?: boolean;
  showPlanLabel?: boolean;
  productType?: string;
  showButtonOnly?: boolean;
  showDeliveryText?: boolean;
  displaySinglePlanLabel?: boolean;
  daysOffer?: boolean;
  isBundle?: boolean;
  ctaText?: string;
  isAvailable?: boolean;
  dontShowDeliveryCost?: boolean;
  basketCouponCode?: string;
  fullWidth?: boolean;
}

const FALLBACK_SHOPIFY_HOST = 'https://vitl.com';

const getNormalizedShopifyHost = () => getShopifyHost() || FALLBACK_SHOPIFY_HOST;

const buildShopifyCartAddUrl = (variantId: string, sellingPlanId: string, couponCode?: string) => {
  const shopifyHost = getNormalizedShopifyHost();
  const returnTo = couponCode ? `/checkout?discount=${couponCode}` : '/checkout';
  return appendStoredTrackingParamsToUrl(`${shopifyHost}/cart/add?items[][id]=${encodeURIComponent(
    variantId
  )}&items[][quantity]=1&items[][selling_plan]=${encodeURIComponent(
    sellingPlanId
  )}&return_to=${encodeURIComponent(returnTo)}`);
};

const appendCheckoutAttributesIfNeeded = (url: string, attributes: Record<string, string>) => {
  if (!Object.keys(attributes).length) {
    return url;
  }

  return appendShopifyAttributesToUrl(url, attributes);
};

const appendDiscountCodeIfNeeded = (url: string, couponCode?: string) => {
  if (!couponCode) return url;

  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.set('discount', couponCode);
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

// Redirect to Shopify cart with a variant id (stored in sku field)
const redirectToShopifyCart = (
  variantId: string,
  sellingPlanId?: string,
  attributes: Record<string, string> = {},
  couponCode?: string
) => {
  if (sellingPlanId) {
    const url = buildShopifyCartAddUrl(variantId, sellingPlanId, couponCode);
    window.location.href = appendCheckoutAttributesIfNeeded(url, attributes);
    return;
  }

  const shopifyHost = getNormalizedShopifyHost();
  const url = appendStoredTrackingParamsToUrl(`${shopifyHost}/cart/${variantId}:1`);
  window.location.href = appendDiscountCodeIfNeeded(
    appendCheckoutAttributesIfNeeded(url, attributes),
    couponCode
  );
};

const getVariantIdFromPlan = (plan?: IProductPlan, isFreeTrial?: boolean) => {
  if (!plan) return undefined;
  if (isFreeTrial) return plan.shopifyTVariantId || plan.shopifyVariantId || plan.sku;
  return plan.shopifyVariantId || plan.sku;
};

const getCouponCodeForAddToBasket = (isFreeTrial: boolean, productType?: string) => {
  if (isFreeTrial && productType === OFFER_CODES.EssentialOne) {
    return 'A-FREE-ONE';
  }

  if (!isFreeTrial && productType) {
    return String(getCookie(COOKIES.couponCode) || '');
  }

  return '';
};

const AddToBasket: React.FC<IProps> = ({
  label = '',
  isSmallAddToBasketButton = false,
  isReorderButton = false,
  sku,
  plans,
  isFreeTrial = false,
  showPlanLabel = false,
  productType,
  showDeliveryText = true,
  showButtonOnly = false,
  displaySinglePlanLabel = false,
  daysOffer = false,
  isBundle = false,
  ctaText,
  isAvailable = true,
  dontShowDeliveryCost,
  fullWidth = false,
}: IProps) => {
  if (!isAvailable) {
    return <h4>{OUT_OF_STOCK}</h4>;
  }
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasRouteOneOff = useQueryParamBoolean('oneOff');

  const defaultPlan = hasRouteOneOff
    ? plans?.find((plan: any) => plan.sku.includes('one-time'))
    : plans?.find((plan: any) => plan.default);

  const { register, handleSubmit } = useForm({
    defaultValues: {
      sku: plans ? defaultPlan?.sku || plans[0]?.sku : sku,
    },
  });
  const { expireOfferSoon } = couponCodeService();

  const buttonLabel = useMemo(() => {
    if (ctaText) {
      return ctaText;
    }
    return label ? label : isFreeTrial ? 'Claim free trial' : 'Add to basket';
  }, [label, isFreeTrial, ctaText]);

  const getSellingPlanIdForSku = (selectedSku: string) =>
    plans?.find((plan: IProductPlan) => plan.sku === selectedSku)?.sellingPlanId;
  const { getShopifyCheckoutAttributes } = useShopifyCheckoutAttributes(isFreeTrial);

  const handleAddToBasket = async (selectedSku: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
    const selectedPlan = plans?.find((plan: IProductPlan) => plan.sku === selectedSku);
    const selectedVariantId = getVariantIdFromPlan(selectedPlan, isFreeTrial) || selectedSku;
    const selectedSellingPlanId = getSellingPlanIdForSku(selectedSku);
    const checkoutAttributes = await getShopifyCheckoutAttributes();
    const storedCouponCode = getCouponCodeForAddToBasket(isFreeTrial, productType);
    expireOfferSoon(60);

    redirectToShopifyCart(
      selectedVariantId,
      selectedSellingPlanId,
      checkoutAttributes,
      storedCouponCode || undefined
    );
    } catch (error) {
      setIsSubmitting(false);
      throw error;
    }
  };

  const handleOptionSubmit = (formData: any) => {
    const selectedSku = formData['sku'];
    void handleAddToBasket(selectedSku);
  };

  const getFormattedPrice = (plan: any) =>
    formatPrice(plan.offerPrice || plan.rrpPrice, plan.currency);

  const addToBasketButtonClass = () => {
    if (isSmallAddToBasketButton) return styles.buttonPrimarySmall;
    if (showButtonOnly) return styles.containerWithoutMargin;
    if (isReorderButton) return styles.reorderButton;
    return styles.container;
  };

  const deliveryCost = () => {
    if (dontShowDeliveryCost) return 'Free UK delivery';
    return <p className={styles.small}>Free UK delivery on subscriptions &amp; orders over £20</p>;
  };

  return (
    <div className={addToBasketButtonClass()}>
      {daysOffer && (
        <p className={styles.daysOffer} data-testid="add-to-basket-offer-price">
          BUY TODAY & SAVE |{' '}
          <span>
            <del>{formatPrice(plans[0]?.rrpPrice, plans[0]?.currency)}</del>
          </span>{' '}
          {formatPrice(plans[0]?.offerPrice, plans[0]?.currency)}
        </p>
      )}

      {plans?.length === 1 && !displaySinglePlanLabel && (
        <div>
          {!showButtonOnly && (
            <p className={styles.priceWrapper} data-testid="add-to-basket-price">
              {plans[0].offerPrice && (
                <span className={styles.oldPrice}>
                  <del>{formatPrice(plans[0].rrpPrice, plans[0].currency)}</del>
                </span>
              )}
              {!isFreeTrial && (
                <span className={styles.price}>
                  {formatPrice(plans[0].offerPrice || plans[0].rrpPrice, plans[0].currency)}
                </span>
              )}
            </p>
          )}
          <SubmitButton
            fullWidth={fullWidth}
            buttonLabel={buttonLabel}
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={() => void handleAddToBasket(plans[0].sku)}
          />
        </div>
      )}

      {(plans?.length > 1 || displaySinglePlanLabel) && (
        <form onSubmit={handleSubmit(handleOptionSubmit)}>
          <div className={styles.radioGroup}>
            {plans.map((plan: any) => (
              <label key={plan.sku} className={styles.radioContainer}>
                <input
                  type="radio"
                  name="sku"
                  value={plan.sku}
                  ref={register({ required: true })}
                />
                <span className={styles.checkmark}></span>

                <div className={styles.radioLabel}>
                  <span>{plan.planLabel}</span>
                  <span>
                    {plan.offerPrice ? (
                      <s className={styles.crossedPrice}>
                        {formatPrice(plan.rrpPrice, plan.currency)}
                      </s>
                    ) : null}
                    {getFormattedPrice(plan)}
                  </span>
                </div>
              </label>
            ))}
          </div>

          <SubmitButton
            fullWidth={fullWidth}
            loading={isSubmitting}
            disabled={isSubmitting}
            buttonLabel={buttonLabel}
          />
        </form>
      )}

      {!showButtonOnly && showDeliveryText && !isReorderButton && (
        <div className={styles.delivery}>
          {showPlanLabel && <p className={styles.small}>{plans[0].planLabel}</p>}
        </div>
      )}

      {isReorderButton && sku && (
        <div onClick={() => void handleAddToBasket(sku)}>{isSubmitting ? 'Loading...' : 'Reorder'}</div>
      )}

      {isSmallAddToBasketButton && sku && showButtonOnly && (
        <div onClick={() => void handleAddToBasket(sku)}>{isSubmitting ? 'Loading...' : buttonLabel}</div>
      )}

      {isBundle && sku && (
        <SubmitButton
          fullWidth={fullWidth}
          buttonLabel={buttonLabel}
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={() => void handleAddToBasket(sku)}
        />
      )}
    </div>
  );
};

export default AddToBasket;
